import { LoadingTypes, startLoading, stopLoading } from '@labrav/common'
import { camelize, snakeize } from 'casing'
import {
  fetchUserProfile,
  getG2ReviewUrl,
  patchUserProfile as oktaPatchUserProfile,
  patchUserProfileIdentityService,
  patchUserWalkthrough,
} from '../../../common/api'
import { RootState, AppDispatch } from '../../../store'
import { errorLogger } from '../../utils/errorLogger'
import {
  RequestFailureMessage,
  UserUpdationFailureMessage,
  UserUpdationSuccessMessage,
} from '../../utils/messagesContants'
import { renameKey } from '../../utils/renameKey'
import { Role } from '../../utils/roles'
import { updateAppAlert } from '../appAlert/actions'
import { AppAlertState } from '../appAlert/reducer'

import {
  AuthProvider,
  getUsers,
} from '../../../oppsync/modules/userList/action'
import { getErrorMessages } from '../../utils/error'
import { AxiosError, AxiosResponse } from 'axios'

import {
  UserProfile,
  UserProfileActionTypes,
  UserWalkthrough,
} from './userProfileTypes'

export type { UserProfile, UserWalkthrough }
export { UserProfileActionTypes }

const errorMappings: Record<string, string> = {
  'Old Password is not correct': 'Current Password is not correct',
}

export type IdentityServiceUser = {
  firstname: string
  lastname: string
  email: string
  timezone?: string
  id: string
  roles: Role[]
  idp_user_id: string
  walkthroughs?: UserWalkthrough
}

const mapProfileToIdentityServicePayload = (
  profile: UserProfile
): Partial<IdentityServiceUser> => ({
  firstname: profile.firstName || '',
  lastname: profile.lastName || '',
  email: profile.email || '',
  timezone: profile.timeZone || '',
})

const mapIdentityServicePayloadToProfile = (
  data: IdentityServiceUser
): UserProfile => ({
  firstName: data.firstname,
  givenName: data.firstname,
  familyName: data.lastname,
  lastName: data.lastname,
  email: data.email,
  timeZone: data.timezone,
  id: data.id,
  idpUserId: data.idp_user_id,
  roles: data.roles,
  walkthroughs: data.walkthroughs,
})

export const getUserProfile =
  (partnerId: string, userId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const userProfile = await fetchUserProfile(partnerId, userId)
      dispatch(setUserProfile(mapIdentityServicePayloadToProfile(userProfile)))
    } catch (e) {
      const error = e as AxiosError<ErrorResponse>
      const errorMessage = getErrorMessages(['Error getting user profile'])(
        error?.response
      )
      const newAlert: AppAlertState = {
        message: errorMessage,
        messageType: 'ERROR',
        autoClose: true,
      }
      dispatch(updateAppAlert(newAlert))

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updateUserProfile =
  (
    partnerId: string,
    idpUserId: string,
    profile: UserProfile,
    authProvider: AuthProvider
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    const patchUserProfile =
      authProvider === AuthProvider.OKTA
        ? oktaPatchUserProfile
        : patchUserProfileIdentityService

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const payload =
      authProvider === AuthProvider.AUTH0
        ? mapProfileToIdentityServicePayload(profile)
        : profile
    return patchUserProfile(partnerId, idpUserId, payload)
      .then(res => {
        const updatedData = renameKey(res.data, 'timezone', 'time_zone')
        dispatch(setUserProfile(updatedData))
        const newAlert: AppAlertState = {
          message: UserUpdationSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        }
        dispatch(updateAppAlert(newAlert))
      })
      .catch((error: any) => {
        const errMessage =
          error.response.data?.message || UserUpdationFailureMessage
        dispatch(setUserProfileError(errorMappings[errMessage] || errMessage))
        const newAlert: AppAlertState = {
          message: errorMappings[errMessage] || errMessage,
          messageType: 'ERROR',
          autoClose: true,
        }
        dispatch(updateAppAlert(newAlert))

        const globalState = getState()
        errorLogger({ globalState })(error as Error)
      })
      .finally(() => {
        dispatch(stopLoading(LoadingTypes.GENERAL))
      })
  }

export const setUserProfile = (data: UserProfile) => ({
  type: UserProfileActionTypes.UPDATE_USER_PROFILE,
  payload: camelize(data),
})

export const setOktaUserId = (id: string) => ({
  type: UserProfileActionTypes.SET_OKTA_USER_ID,
  payload: id,
})

export const setUserProfileError = (error: any) => ({
  type: UserProfileActionTypes.UPDATE_USER_PROFILE_ERROR,
  payload: error,
})

export const setDefaultTimeZone = (defaultTimeZone: string) => ({
  type: UserProfileActionTypes.SET_DEFAULT_TIMEZONE,
  payload: defaultTimeZone,
})

export const fetchG2ReviewUrl =
  ({ partnerId, userId }: { partnerId: string; userId: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getG2ReviewUrl(partnerId, userId)
      return { url: response?.data?.g2_url }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updateUserWalkthrough =
  ({
    partnerId,
    userId,
    data,
    userPartnerId,
  }: {
    partnerId: string
    userId: string
    data: UserWalkthrough
    userPartnerId: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await patchUserWalkthrough(partnerId, userId, {
        walkthroughs: snakeize(data),
      })
      await dispatch(getUserProfile(partnerId, userPartnerId))
      await dispatch(
        getUsers({
          partnerId,
          pageNumber: 1,
          pageSize: 500,
          authProvider: AuthProvider.AUTH0,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    }
  }
